import React from "react";
import { ExpandMore, HelpOutline, MoreVert } from "@mui/icons-material";
import { Fullscreen } from "@mui/icons-material";
import { Chat } from "@mui/icons-material";
import { Dashboard } from '@mui/icons-material';
import { Box, MenuItem, CircularProgress, Menu } from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import { useTranslation } from "react-i18next";
import { StyledMenu, StyledTooltip, StyledIconButton } from './Chat.styles';
import { FileDownload } from "@mui/icons-material";
import { Message } from "../../types/chat";
import { useOnboarding } from '../../context/OnboardingContext';
import { Z_INDEX } from './Chat.styles';
import { AppManagerLogo } from '../AppManagerLogo';
import { useApi } from '../../hooks/useApi';
import { useInvalidLinks } from "../../context/InvalidLinksContext";
import { exportToPDF } from "../../utils/exportPDF";

interface ChatHeaderProps {
  isFullscreen: boolean;
  isSidePanel: boolean;
  setIsFullscreen: (value: boolean) => void;
  setSidePanel: (value: boolean) => void;
  onToggleCollapse?: () => void;
  onViewModeChange: (mode: 'chat' | 'sidepanel' | 'fullscreen') => void;
  isMobile: boolean;
  messages: Message[];
}


export const ChatHeader: React.FC<ChatHeaderProps> = ({
  isFullscreen,
  isSidePanel,
  onToggleCollapse,
  onViewModeChange,
  isMobile,
  messages,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { fetchWithSession } = useApi();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = React.useState(false);
  const [isNarrow, setIsNarrow] = React.useState(false);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const { openModal, hasSeenOnboarding, getHasSeenOnboardingLocal } = useOnboarding();
  const { invalidLinksForPDF } = useInvalidLinks();
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  // Check if header width is narrow
  React.useEffect(() => {
    const checkWidth = () => {
      if (headerRef.current) {
        setIsNarrow(headerRef.current.offsetWidth < 400);
      }
    };

    checkWidth();
    const resizeObserver = new ResizeObserver(checkWidth);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewModeSelect = (mode: 'chat' | 'sidepanel' | 'fullscreen') => {
    onViewModeChange(mode);
    handleClose();
  };

  const getCurrentMode = () => {
    if (isFullscreen) return 'fullscreen';
    if (isSidePanel) return 'sidepanel';
    return 'chat';
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      await exportToPDF({
        messages,
        fetchWithSession,
        t,
        invalidLinksForPDF,
        setSnackbarMessage,
        setSnackbarOpen
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box ref={headerRef} display="flex" justifyContent="space-between" p={isSidePanel ? 1.3 : 2} borderBottom="1px solid rgba(0,0,0,0.12)">
      <AppManagerLogo
        height={getCurrentMode() === 'fullscreen' || getCurrentMode() === 'sidepanel' ? 28 : 24}
      />
      <Box>
        {(isMobile || isNarrow) ? (
          <>
            <StyledTooltip
              title={t('menu')}
              PopperProps={{
                disablePortal: false,
                sx: { zIndex: 99999999 }
              }}
            >
              <StyledIconButton
                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                aria-label={t('menu')}
              >
                <MoreVert />
              </StyledIconButton>
            </StyledTooltip>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
              sx={{ zIndex: Z_INDEX.DROPDOWNS }}
            >
              {(hasSeenOnboarding || getHasSeenOnboardingLocal()) && (
                <MenuItem onClick={() => {
                  openModal();
                  setMenuAnchorEl(null);
                }}>
                  <HelpOutline sx={{ mr: 1 }} />
                  {t('help')}
                </MenuItem>
              )}
              {getCurrentMode() !== 'chat' && (
                <MenuItem onClick={() => {
                  handleViewModeSelect('chat');
                  setMenuAnchorEl(null);
                }}>
                  <Chat sx={{ mr: 1 }} /> {t('chat')}
                </MenuItem>
              )}
              {getCurrentMode() !== 'sidepanel' && (
                <MenuItem onClick={() => {
                  handleViewModeSelect('sidepanel');
                  setMenuAnchorEl(null);
                }}>
                  <VerticalSplitIcon sx={{ mr: 1 }} /> {t('enterSidePanel')}
                </MenuItem>
              )}
              {getCurrentMode() !== 'fullscreen' && (
                <MenuItem onClick={() => {
                  handleViewModeSelect('fullscreen');
                  setMenuAnchorEl(null);
                }}>
                  <Fullscreen sx={{ mr: 1 }} /> {t('enterFullscreen')}
                </MenuItem>
              )}
              <MenuItem onClick={() => {
                handleExportPDF();
                setMenuAnchorEl(null);
              }}
              disabled={isExporting || messages.length < 2}
              >
                {isExporting ? <CircularProgress size={24} sx={{ mr: 1 }} /> : <FileDownload sx={{ mr: 1 }} />}
                {t('exportReport')}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            {(hasSeenOnboarding || getHasSeenOnboardingLocal()) && (
              <StyledTooltip
                title={t('help')}
                PopperProps={{
                  disablePortal: false,
                  sx: { zIndex: 99999999 }
                }}
              >
                <StyledIconButton
                  onClick={openModal}
                  aria-label={t('help')}
                >
                  <HelpOutline />
                </StyledIconButton>
              </StyledTooltip>)}
            <StyledTooltip
              title={t('changeViewMode')}
              PopperProps={{
                disablePortal: false,
                sx: { zIndex: 99999999 }
              }}
            >
              <StyledIconButton
                onClick={handleClick}
                aria-label={t('changeViewMode')}
                aria-controls="view-mode-menu"
                aria-haspopup="true"
              >
                <Dashboard />
              </StyledIconButton>
            </StyledTooltip>
            <StyledMenu
              className="change-view-mode-tooltip"
              id="view-mode-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              {getCurrentMode() !== 'chat' && (
                <MenuItem onClick={() => handleViewModeSelect('chat')}>
                  <Chat sx={{ mr: 1 }} /> {t('chat')}
                </MenuItem>
              )}
              {getCurrentMode() !== 'sidepanel' && (
                <MenuItem onClick={() => handleViewModeSelect('sidepanel')}>
                  <VerticalSplitIcon sx={{ mr: 1 }} /> {t('enterSidePanel')}
                </MenuItem>
              )}
              {getCurrentMode() !== 'fullscreen' && (
                <MenuItem onClick={() => handleViewModeSelect('fullscreen')}>
                  <Fullscreen sx={{ mr: 1 }} /> {t('enterFullscreen')}
                </MenuItem>
              )}
            </StyledMenu>
            {!isExporting ? (
              <StyledTooltip
                title={t('exportReport')}
                PopperProps={{
                  disablePortal: false,
                  sx: { zIndex: 99999999 }
                }}
              >
                <StyledIconButton
                  onClick={handleExportPDF}
                  aria-label={t('exportReport')}
                  disabled={isExporting}
                >
                  <FileDownload />
                </StyledIconButton>
              </StyledTooltip>
            ) : (
              <StyledTooltip
                title={t('loading')}
                PopperProps={{
                  disablePortal: false,
                  sx: { zIndex: 99999999 }
                }}
              >
                <CircularProgress size={20} />
              </StyledTooltip>
            )}
          </>
        )}
        <StyledTooltip
          title={t('collapseChat')}
          PopperProps={{
            disablePortal: false,
            sx: { zIndex: 99999999 }
          }}
        >
          <StyledIconButton onClick={onToggleCollapse} aria-label={t('collapseChat')}>
            <ExpandMore />
          </StyledIconButton>
        </StyledTooltip>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};